/**
 * Vault attachment crypto, performed server-side through the vault API.
 *
 * On macOS, sealing a vault note rewrites each attachment reference to an
 * opaque blob under `VaultAttachments/<note-id>/<uuid>.vault`, and keeps only
 * the original relative path inside the note's encrypted payload
 * (`Manifest.originalRelativePath`). That path is relative to the attachments
 * root and has the shape `<attachmentID>/<filename>` — exactly the portable
 * `jnote-att://<attachmentID>/<filename>` reference.
 *
 * The Web client has no filesystem to restore into, so a vault attachment is
 * served as a fresh object URL created from the decrypted bytes for the
 * lifetime of the unlocked session.
 */
import { storageApi } from '../api/storage.js';
import { vaultApi, type VaultAttachmentManifest } from '../api/vault.js';
import { attachmentURL } from '../sync/attachmentCodec.js';
import { vaultAttachmentKey } from '../sync/paths.js';

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export interface DecryptedVaultAttachment {
  /** Portable `jnote-att://<id>/<filename>` reference used in note bodies. */
  reference: string;
  filename: string;
  mimeType: string;
  url: string;
}

/** Guesses a MIME type from a filename, for building usable blob URLs. */
export function guessMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const known: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    heic: 'image/heic',
    pdf: 'application/pdf',
    txt: 'text/plain',
    md: 'text/markdown',
    json: 'application/json',
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
  };
  return known[ext] ?? 'application/octet-stream';
}

/**
 * Splits `originalRelativePath` into its attachment id and filename.
 *
 * Guards against traversal (`../`) and absolute paths, which the macOS client
 * also rejects via `attachmentRelativePath`.
 */
export function parseRelativePath(relativePath: string): { id: string; filename: string } | null {
  const parts = relativePath.split('/').filter((part) => part.length > 0);
  if (parts.length !== 2) return null;
  const [id, filename] = parts as [string, string];
  if (id === '.' || id === '..' || filename === '.' || filename === '..') return null;
  return { id, filename };
}

/**
 * Decrypts every vault attachment of a note and returns object URLs keyed by
 * their portable reference, for rewriting into the note body.
 */
export async function decryptVaultAttachments(
  noteId: string,
  manifests: VaultAttachmentManifest[],
): Promise<Map<string, DecryptedVaultAttachment>> {
  const result = new Map<string, DecryptedVaultAttachment>();

  for (const manifest of manifests) {
    const parsed = parseRelativePath(manifest.originalRelativePath);
    if (!parsed) continue;

    let bytes: Uint8Array;
    try {
      const ciphertext = await storageApi.get(
        vaultAttachmentKey(noteId, manifest.encryptedName),
      );
      const { dataBase64 } = await vaultApi.decryptBytes(toBase64(ciphertext));
      bytes = fromBase64(dataBase64);
    } catch {
      // A missing or tampered blob must not block rendering the whole note.
      continue;
    }

    const mimeType = guessMimeType(parsed.filename);
    const blob = new Blob([bytes as BlobPart], { type: mimeType });
    const url = URL.createObjectURL(blob);
    result.set(attachmentURL(parsed.id, parsed.filename), {
      reference: attachmentURL(parsed.id, parsed.filename),
      filename: parsed.filename,
      mimeType,
      url,
    });
  }

  return result;
}

/** Releases object URLs created by `decryptVaultAttachments`. */
export function revokeVaultAttachmentURLs(
  attachments: Map<string, DecryptedVaultAttachment>,
): void {
  for (const attachment of attachments.values()) URL.revokeObjectURL(attachment.url);
}

/** Encrypts a new attachment for a vault note and uploads its ciphertext. */
export async function encryptVaultAttachment(
  noteId: string,
  originalRelativePath: string,
  data: Uint8Array,
  encryptedName = `${randomName()}.vault`,
): Promise<VaultAttachmentManifest> {
  const { ciphertextBase64 } = await vaultApi.encryptBytes(toBase64(data));
  await storageApi.put(vaultAttachmentKey(noteId, encryptedName), fromBase64(ciphertextBase64));
  return { originalRelativePath, encryptedName };
}

function randomName(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
